/**
 * The print contract — the seam between this SPA and the PDF export endpoint.
 *
 * The export renders THIS route in headless Chromium and prints it. There is no
 * second report template in Python: whatever paper shows, the screen shows too,
 * because both come out of the same `<Report>` component.
 *
 * ── What the exporter gives us ──────────────────────────────────────────────
 * Before the bundle boots, the exporter may inject
 *
 *   window.__BAUPILOT_PRINT__ = {
 *     report: RunReport,     // exactly the GET /api/v1/runs/{id}/report body
 *     run:    Run | null,    //         the GET /api/v1/runs/{id} body
 *     diff:   RunDiff | null,
 *     locale: "de" | "en",
 *     generated_at: "<ISO-8601 UTC>",
 *   }
 *
 * When that global is present the print view renders straight from it and makes
 * NO network request — no session, no async, no 401 at render time. When it is
 * absent (a human opening the route in their browser) the view fetches through
 * the ordinary authenticated client instead. Same component either way.
 *
 * Because injection already says "this is a print render", the app switches into
 * the print view on the strength of the global alone — the exporter does not
 * have to serve the bundle under the print path. The path still works for
 * humans: /lauf/:runId/bericht/druck.
 *
 * ── What we give the exporter back ─────────────────────────────────────────
 *   · `document.documentElement.dataset.printReady = '1'` once the page has
 *     rendered AND its self-hosted fonts have loaded — wait for that instead of
 *     guessing a timeout;
 *   · `window.__BAUPILOT_PRINT_META__` with the document title and ready-made
 *     Chromium header/footer templates (case · Gemeinde · date · page number),
 *     for `Page.printToPDF` with `displayHeaderFooter: true`.
 *
 * A4 itself comes from the CSS (`@page { size: A4 }` in src/index.css), not
 * from a flag on the Python side.
 */
import type { Run, RunDiff, RunReport } from '@/api/types'

/** Injected by the PDF export before the bundle boots. */
export interface InjectedPrintPayload {
  report: RunReport
  run?: Run | null
  diff?: RunDiff | null
  locale?: 'de' | 'en'
  generated_at?: string
}

/** Handed back to the exporter once the page is on screen. */
export interface PrintMeta {
  contract: 1
  ready: boolean
  runId: string
  documentTitle: string
  locale: string
  caseLabel: string
  gemeinde: string
  zone: string
  date: string
  /** Ready for Chromium `Page.printToPDF`. */
  headerTemplate: string
  footerTemplate: string
  margin: { top: string; right: string; bottom: string; left: string }
}

declare global {
  interface Window {
    __BAUPILOT_PRINT__?: InjectedPrintPayload
    __BAUPILOT_PRINT_META__?: PrintMeta
  }
}

/** The ready flag the exporter polls. */
export const PRINT_READY_ATTR = 'data-print-ready'

/**
 * Page margins. Top and bottom leave room for Chromium's running header and
 * footer; they match the `@page` margins in src/index.css so a plain Ctrl+P
 * produces the same block on paper.
 */
export const PRINT_MARGIN = {
  top: '20mm',
  right: '14mm',
  bottom: '18mm',
  left: '14mm',
} as const

/** Read the injected payload, defensively — a malformed global is ignored. */
export function injectedPrintPayload(): InjectedPrintPayload | null {
  if (typeof window === 'undefined') return null
  const raw = window.__BAUPILOT_PRINT__
  if (!raw || typeof raw !== 'object') return null
  if (!raw.report || typeof raw.report !== 'object') return null
  return raw
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Chromium header/footer templates.
 *
 * Chromium renders these with `font-size: 0` unless it is set explicitly, and
 * it only substitutes its own classes (`pageNumber`, `totalPages`, `date`,
 * `title`, `url`). Everything else is literal HTML — so the case, the Gemeinde
 * and the run date are baked in here, and only the page number comes from the
 * browser. No URL is ever placed on paper.
 */
export function headerTemplate(meta: {
  caseLabel: string
  gemeinde: string
  gemeindeLabel: string
}): string {
  return (
    `<div style="font-family:'Inter','Helvetica Neue',Arial,sans-serif;font-size:7.5pt;color:#5a6e63;` +
    `width:100%;padding:0 14mm;display:flex;justify-content:space-between;gap:8mm;">` +
    `<span style="color:#1e3a5f;font-weight:600;">Baueingabe-Check-KAi — Pre-Check Baueingabe</span>` +
    `<span>${escapeHtml(meta.caseLabel)} · ${escapeHtml(meta.gemeindeLabel)} ${escapeHtml(meta.gemeinde)}</span>` +
    `</div>`
  )
}

export function footerTemplate(meta: {
  date: string
  dateLabel: string
  pageLabel: string
  ofLabel: string
  attribution: string
}): string {
  return (
    `<div style="font-family:'Inter','Helvetica Neue',Arial,sans-serif;font-size:7.5pt;color:#5a6e63;` +
    `width:100%;padding:0 14mm;display:flex;justify-content:space-between;gap:8mm;">` +
    `<span>${escapeHtml(meta.dateLabel)}: ${escapeHtml(meta.date)} · ${escapeHtml(meta.attribution)}</span>` +
    `<span>${escapeHtml(meta.pageLabel)} <span class="pageNumber"></span> ${escapeHtml(meta.ofLabel)} <span class="totalPages"></span></span>` +
    `</div>`
  )
}

/** Swiss date, stable regardless of the browser's locale settings. */
const SWISS_DATE = new Intl.DateTimeFormat('de-CH', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

export function printDate(iso?: string | null): string {
  const date = iso ? new Date(iso) : new Date()
  // An unparseable timestamp falls back to today rather than printing
  // "Invalid Date" onto a document someone hands to a Bauamt.
  if (Number.isNaN(date.getTime())) return SWISS_DATE.format(new Date())
  return SWISS_DATE.format(date)
}
